import { Archive, Pause, Plus, RefreshCw, Users } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import type { ClientFormData, CoachingClient } from '../problems/types'
import { AddClientModal } from './add-client-modal'
import { ClientCard } from './client-card'

interface ClientListProps {
  clients: CoachingClient[]
  loading: boolean
  error: string | null
  onAddClient: (data: ClientFormData) => Promise<CoachingClient>
  onEditClient: (
    id: string,
    data: Partial<ClientFormData>,
  ) => Promise<CoachingClient>
  onRefresh: () => Promise<void>
}

export function ClientList({
  clients,
  loading,
  error,
  onAddClient,
  onEditClient,
  onRefresh,
}: ClientListProps) {
  const [activeTab, setActiveTab] = useState<'active' | 'archived'>('active')
  const [isModalOpen, setIsModalOpen] = useState(false)

  const { activeClients, pausedClients, archivedClients } = useMemo(() => {
    const active: CoachingClient[] = []
    const paused: CoachingClient[] = []
    const archived: CoachingClient[] = []

    clients.forEach((client) => {
      if (client.status === 'active') {
        active.push(client)
      } else if (client.status === 'paused') {
        paused.push(client)
      } else if (client.status === 'archived') {
        archived.push(client)
      }
    })

    // Sort by oldest check-in first (needs attention at top)
    // No check-in = needs most attention, ties broken by name
    const sortByCheckIn = (a: CoachingClient, b: CoachingClient) => {
      const aCheckIn = a.lastCheckInAt ? new Date(a.lastCheckInAt).getTime() : 0
      const bCheckIn = b.lastCheckInAt ? new Date(b.lastCheckInAt).getTime() : 0

      // No check-in (0) should be at top, so treat as oldest
      if (aCheckIn === 0 && bCheckIn !== 0) return -1
      if (bCheckIn === 0 && aCheckIn !== 0) return 1

      // Both have check-ins: oldest first
      if (aCheckIn !== bCheckIn) return aCheckIn - bCheckIn

      // Tie-breaker: alphabetical by name
      return a.name.localeCompare(b.name)
    }

    return {
      activeClients: active.sort(sortByCheckIn),
      pausedClients: paused.sort(sortByCheckIn),
      archivedClients: archived.sort(sortByCheckIn),
    }
  }, [clients])

  const handleRestoreClient = async (client: CoachingClient) => {
    await onEditClient(client.id, { status: 'active' })
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-muted-foreground flex items-center gap-3">
          <RefreshCw size={20} className="animate-spin" />
          <span>Loading clients...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen">
        {/* Header */}
        <header className="bg-background/95 border-border sticky top-0 z-10 border-b backdrop-blur-sm">
          <div className="mx-auto max-w-6xl px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-gradient-to-br from-sky-500 to-indigo-600 p-2">
                  <Users size={24} className="text-white" />
                </div>
                <div>
                  <h1 className="text-foreground text-xl font-bold">
                    Coaching Clients
                  </h1>
                  <p className="text-muted-foreground text-sm">
                    0 total clients
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Button onClick={() => setIsModalOpen(true)}>
                  <Plus size={18} />
                  Add Client
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Error State with Add Client option */}
        <main className="mx-auto max-w-6xl px-6 py-6">
          <div className="py-16 text-center">
            <div className="bg-muted mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full">
              <Users size={32} className="text-muted-foreground" />
            </div>
            <h3 className="text-foreground mb-2 text-lg font-medium">
              Unable to load clients
            </h3>
            <p className="text-muted-foreground mx-auto mb-4 max-w-md">
              {error}. You can still add your first client below.
            </p>
            <div className="flex justify-center gap-3">
              <Button onClick={onRefresh} variant="outline">
                <RefreshCw size={18} />
                Retry
              </Button>
              <Button onClick={() => setIsModalOpen(true)}>
                <Plus size={18} />
                Add Client
              </Button>
            </div>
          </div>
        </main>

        {/* Add Client Modal */}
        <AddClientModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSubmit={onAddClient}
        />
      </div>
    )
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="bg-background/95 border-border sticky top-0 z-10 border-b backdrop-blur-sm">
        <div className="mx-auto max-w-6xl px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-gradient-to-br from-sky-500 to-indigo-600 p-2">
                <Users size={24} className="text-white" />
              </div>
              <div>
                <h1 className="text-foreground text-xl font-bold">
                  Coaching Clients
                </h1>
                <p className="text-muted-foreground text-sm">
                  {clients.length} total clients
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button onClick={() => setIsModalOpen(true)}>
                <Plus size={18} />
                Add Client
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Tab Navigation */}
      <div className="mx-auto max-w-6xl px-6 pt-6">
        <div className="bg-muted border-border flex w-fit gap-1 rounded-lg border p-1">
          <button
            type="button"
            onClick={() => setActiveTab('active')}
            className={`flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === 'active'
                ? 'bg-background text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Users size={16} />
            Active & Paused
            <span className="bg-muted-foreground/20 ml-1 rounded px-1.5 py-0.5 text-xs">
              {
                clients.filter(
                  (c) => c.status === 'active' || c.status === 'paused',
                ).length
              }
            </span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('archived')}
            className={`flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === 'archived'
                ? 'bg-background text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Archive size={16} />
            Archived
            <span className="bg-muted-foreground/20 ml-1 rounded px-1.5 py-0.5 text-xs">
              {clients.filter((c) => c.status === 'archived').length}
            </span>
          </button>
        </div>
      </div>

      {/* Client List */}
      <main className="mx-auto max-w-6xl px-6 py-6">
        {activeTab === 'active' ? (
          activeClients.length === 0 && pausedClients.length === 0 ? (
            <div className="py-16 text-center">
              <div className="bg-muted mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full">
                <Users size={32} className="text-muted-foreground" />
              </div>
              <h3 className="text-foreground mb-2 text-lg font-medium">
                No active clients
              </h3>
              <p className="text-muted-foreground">
                Add your first client to get started.
              </p>
              <Button onClick={() => setIsModalOpen(true)} className="mt-4">
                <Plus size={18} />
                Add Client
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Active Clients Section */}
              {activeClients.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Users size={16} className="text-muted-foreground/60" />
                    <h2 className="text-muted-foreground/70 text-sm font-medium uppercase tracking-wide">
                      Active
                    </h2>
                    <span className="bg-muted-foreground/20 text-muted-foreground rounded-full px-2.5 py-0.5 text-xs font-medium">
                      {activeClients.length}
                    </span>
                  </div>
                  {activeClients.map((client) => (
                    <div key={client.id} className="relative">
                      <ClientCard client={client} />
                    </div>
                  ))}
                </div>
              )}

              {/* Paused Clients Section */}
              {pausedClients.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Pause size={16} className="text-muted-foreground/60" />
                    <h2 className="text-muted-foreground/70 text-sm font-medium uppercase tracking-wide">
                      Paused
                    </h2>
                    <span className="bg-muted-foreground/20 text-muted-foreground rounded-full px-2.5 py-0.5 text-xs font-medium">
                      {pausedClients.length}
                    </span>
                  </div>
                  {pausedClients.map((client) => (
                    <div key={client.id} className="relative">
                      <ClientCard client={client} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        ) : archivedClients.length === 0 ? (
          <div className="py-16 text-center">
            <div className="bg-muted mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full">
              <Archive size={32} className="text-muted-foreground" />
            </div>
            <h3 className="text-foreground mb-2 text-lg font-medium">
              No archived clients
            </h3>
            <p className="text-muted-foreground">
              Archived clients will appear here.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {archivedClients.map((client) => (
              <div key={client.id} className="relative">
                <ClientCard client={client} />
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault()
                    handleRestoreClient(client)
                  }}
                  className="bg-primary text-primary-foreground absolute top-1/2 right-14 -translate-y-1/2 rounded-md px-3 py-1.5 text-xs font-medium hover:opacity-90"
                >
                  Restore
                </button>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Add Client Modal */}
      <AddClientModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={onAddClient}
      />
    </div>
  )
}
